import React, { useState, useEffect, useRef } from 'react';
import { Eye, Volume2, VolumeX } from 'lucide-react';
import { demoCover, FAME_TIERS, getFameTierFromPoints, fameBorderClass } from './liveTheme';

/* ═══════════════════════════════════════════════════════════════════════════════
   LIVE LOBBY CARD — Mosaico Vivo + Marcos de Fama
   
   Features:
   - 13-second animated "live" preview shimmer (Mosaico Vivo effect)
   - Fame tier borders (LoL-style: Bronze → Challenger)
   - No donation amount shown (private for host only)
   - Visual tier indicators
═══════════════════════════════════════════════════════════════════════════════ */

/* ── Fame Border Effects ── */
const FameBorderEffect = ({ tier, children }) => {
  const tierData = FAME_TIERS[tier] || FAME_TIERS.bronze;
  
  // Different effects based on tier
  const effects = {
    none: null,
    shimmer: (
      <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer" />
      </div>
    ),
    glow: (
      <div className="absolute -inset-0.5 rounded-2xl bg-gradient-to-r from-[#D4A853]/20 via-[#D4A853]/10 to-[#D4A853]/20 opacity-50 blur-sm pointer-events-none" />
    ),
    frost: (
      <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-[#4ECDC4]/10 via-transparent to-[#4ECDC4]/5" />
        <div className="absolute top-0 left-0 w-full h-full">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-[#4ECDC4]/60 rounded-full animate-frost-particle"
              style={{
                left: `${20 + i * 30}%`,
                top: `${10 + i * 20}%`,
                animationDelay: `${i * 0.5}s`,
              }}
            />
          ))}
        </div>
      </div>
    ),
    sparkle: (
      <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1.5 h-1.5 bg-[#45B7D1] rounded-full animate-sparkle"
            style={{
              left: `${10 + i * 20}%`,
              top: `${15 + (i % 3) * 25}%`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
      </div>
    ),
    pulse: (
      <div className="absolute -inset-1 rounded-2xl pointer-events-none">
        <div className="absolute inset-0 rounded-2xl bg-[#9B59B6]/20 animate-pulse-ring" />
      </div>
    ),
    flames: (
      <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
        <div className="absolute bottom-0 left-0 right-0 h-1/3">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute bottom-0 w-3 bg-gradient-to-t from-[#E74C3C] via-[#F39C12] to-transparent rounded-full animate-flame"
              style={{
                left: `${5 + i * 12}%`,
                height: `${40 + (i % 3) * 20}%`,
                animationDelay: `${i * 0.1}s`,
                opacity: 0.6,
              }}
            />
          ))}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#E74C3C]/10 via-transparent to-[#D4A853]/5" />
      </div>
    ),
  };

  return (
    <div className="relative">
      {effects[tierData.effect]}
      {children}
    </div>
  );
};

/* ── Mosaico Vivo Preview ── */
const MosaicoVivoPreview = ({ cover, isLive }) => {
  const [phase, setPhase] = useState(0);
  
  // Cycle through phases every 13 seconds to simulate "live" preview
  useEffect(() => {
    if (!isLive) return;
    const interval = setInterval(() => {
      setPhase(p => (p + 1) % 4);
    }, 3250); // 13s / 4 phases
    return () => clearInterval(interval);
  }, [isLive]);

  return (
    <div className="absolute inset-0">
      {/* Base cover image */}
      <img 
        src={cover} 
        alt="" 
        className="absolute inset-0 w-full h-full object-cover" 
        loading="lazy" 
      />
      
      {/* Mosaico Vivo animated overlay when live */}
      {isLive && (
        <>
          {/* Subtle zoom/pan effect */}
          <div 
            className="absolute inset-0 w-full h-full transition-transform duration-[3000ms] ease-in-out"
            style={{
              transform: `scale(${1 + phase * 0.02}) translate(${phase % 2 === 0 ? '1%' : '-1%'}, ${phase % 2 === 0 ? '1%' : '-1%'})`,
            }}
          >
            <img 
              src={cover} 
              alt="" 
              className="w-full h-full object-cover opacity-30" 
              loading="lazy"
            />
          </div>
          
          {/* Breathing glow effect */}
          <div 
            className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30"
            style={{
              opacity: 0.5 + (phase % 2) * 0.2,
              transition: 'opacity 3s ease-in-out',
            }}
          />
          
          {/* Live scan line */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div 
              className="absolute w-full h-0.5 bg-gradient-to-r from-transparent via-red-500/40 to-transparent animate-scan-line"
            />
          </div>
          
          {/* Corner pulse indicators */}
          <div className="absolute top-2 left-2 w-2 h-2 bg-red-500/50 rounded-full animate-ping" style={{ animationDuration: '2s' }} />
        </>
      )}
    </div>
  );
};

/* ── Fame Tier Badge ── */
const FameTierBadge = ({ fameTier, lang }) => {
  const tier = FAME_TIERS[fameTier] || FAME_TIERS.bronze;
  
  // Only show badge for Gold and above
  if (['bronze', 'silver'].includes(tier.id)) return null;
  
  return (
    <div 
      data-testid={`fame-badge-${tier.id}`}
      className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full backdrop-blur-md"
      style={{ 
        backgroundColor: `${tier.color}20`,
        borderColor: `${tier.color}50`,
        borderWidth: 1,
      }}
    >
      <span 
        className="text-[9px] font-bold uppercase tracking-wider"
        style={{ color: tier.color }}
      >
        {lang === 'es' ? tier.name : tier.nameEn}
      </span>
    </div>
  );
};

/* ── Main Lobby Card ── */
export const LiveLobbyCard = ({ session, lang, onOpen, featured = false, index = 0 }) => {
  const isShow = (session?.session_format || 'room') === 'show';
  const cover = session.cover_image || session.artist_photo || demoCover(index);
  
  // Get fame tier from host's pulse points
  const hostPulsePoints = session?.host_pulse_points || 0;
  const fameTier = getFameTierFromPoints(hostPulsePoints);

  return (
    <FameBorderEffect tier={fameTier.id}>
      <button
        data-testid={`pulse-live-card-${session.id}`}
        onClick={() => onOpen(session)}
        className={`group relative w-full overflow-hidden rounded-2xl ${fameBorderClass(fameTier.id)} ${featured ? 'aspect-[4/5]' : 'aspect-[3/4]'} transition-all duration-500 hover:scale-[1.02] hover:brightness-110 bg-[#111]`}
      >
        {/* Mosaico Vivo Preview */}
        <MosaicoVivoPreview cover={cover} isLive={session.status === 'live'} />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />

        {/* Viewer count - top left */}
        <div className="absolute top-3 left-3 flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur-sm px-2.5 py-1">
          <Eye size={11} className="text-white/60" />
          <span className="text-[11px] text-white/70 font-medium">{session.viewers || 0}</span>
        </div>
        
        {/* LIVE badge - top right */}
        <div className="absolute top-3 right-3 flex items-center gap-1 rounded-full bg-red-500/90 px-2 py-0.5">
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
          <span className="text-[9px] font-bold text-white uppercase tracking-wider">Live</span>
        </div>

        {/* Fame tier badge - bottom right (Gold+) */}
        <FameTierBadge fameTier={fameTier.id} lang={lang} />

        {/* Info - bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-3.5">
          {/* Tier accent line */}
          {fameTier.id !== 'bronze' && (
            <div 
              className="w-8 h-[2px] rounded-full mb-2.5"
              style={{ backgroundColor: fameTier.color }}
            />
          )}
          
          <p data-testid={`pulse-live-card-host-${session.id}`} className="text-sm font-semibold text-white truncate">
            {session.artist_name || session.host_name}
          </p>
          <p data-testid={`pulse-live-card-title-${session.id}`} className="text-[12px] text-white/45 truncate mt-0.5">
            {session.title}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <span className={`text-[9px] uppercase tracking-widest font-medium ${isShow ? 'text-[#d4a853]' : 'text-white/30'}`}>
              {isShow ? 'Show' : 'Room'}
            </span>
            {/* NO donation amount shown - it's private */}
          </div>
        </div>
      </button>
      
      {/* CSS Animations */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        
        @keyframes frost-particle {
          0%, 100% { opacity: 0; transform: translateY(0); }
          50% { opacity: 1; transform: translateY(-10px); }
        }
        .animate-frost-particle {
          animation: frost-particle 3s infinite;
        }
        
        @keyframes sparkle {
          0%, 100% { opacity: 0; transform: scale(0); }
          50% { opacity: 1; transform: scale(1); }
        }
        .animate-sparkle {
          animation: sparkle 1.5s infinite;
        }
        
        @keyframes pulse-ring {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.02); }
        }
        .animate-pulse-ring {
          animation: pulse-ring 2s infinite;
        }
        
        @keyframes flame {
          0%, 100% { transform: scaleY(1) translateY(0); opacity: 0.6; }
          50% { transform: scaleY(1.2) translateY(-5px); opacity: 0.8; }
        }
        .animate-flame {
          animation: flame 0.5s infinite;
        }
        
        @keyframes scan-line {
          0% { top: 0; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        .animate-scan-line {
          animation: scan-line 4s infinite;
        }
      `}</style>
    </FameBorderEffect>
  );
};
